import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { Cur } from "@/components/Statement";
import { DateField } from "@/components/DateField";
import { todayInBaghdad } from "@/lib/time";

type Kind = "payment" | "debit_adj" | "credit_adj";
const newOp = () => (crypto as any).randomUUID?.() || `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block text-xs font-bold space-y-1">{label}{children}</label>
);
const Pills = <T extends string>({ value, options, onChange, id, label }: { value: T; options: T[]; onChange: (v: T) => void; id: string; label: (v: T) => string }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${id}-${o}`} aria-pressed={value === o}
        className={`h-9 px-4 rounded-full text-xs font-bold ${value === o ? "bg-foreground text-background" : "bg-accent"}`}>{label(o)}</button>
    ))}
  </div>
);

export const LedgerEntryDialog = ({ accountId, initialKind, onClose, onSaved }: { accountId: string; initialKind: Kind; onClose: () => void; onSaved: () => void }) => {
  const { t } = usePrefs();
  const [kind, setKind] = useState<Kind>(initialKind);
  const [f, setF] = useState({ currency: "IQD" as Cur, amount: "", reason: "", method: "", reference: "", date: "", booking: "" });
  const [other, setOther] = useState(false);
  const [st, setSt] = useState({ amount: "", rate: "", direction: "", approved_by: "" });
  const [busy, setBusy] = useState(false);
  const op = useRef(newOp());
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const otherCur: Cur = f.currency === "IQD" ? "USD" : "IQD";
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${accountId}/ledger`, {
        op_id: op.current, kind, currency: f.currency, amount: f.amount.trim(), reason: f.reason, method: f.method, reference: f.reference,
        effective_date: f.date || null, booking_id: f.booking.trim() || null,
        settlement: kind === "payment" && other ? { ...st, currency: otherCur } : null,
      });
      toast.success(t(kind === "payment" ? "rc.saved" : "b7.saved"));
      onSaved();
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };
  const cls = "h-10 rounded-2xl";
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent data-testid="ledger-dialog" className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-start"><DialogTitle className="font-display">{t(kind === "payment" ? "b7.addPayment" : "b7.addAdjust")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label={t("b7.kindLabel")}><Pills id="ledger-kind" value={kind} options={["payment", "debit_adj", "credit_adj"] as Kind[]} onChange={(v) => setKind(v as Kind)} label={(k) => t(`b7.kind.${k}`)} /></Field>
          <Field label={t("b7.currency")}><Pills id="ledger-currency" value={f.currency} options={["IQD", "USD"] as Cur[]} onChange={(c) => setF({ ...f, currency: c })} label={(c) => c} /></Field>
          <Field label={t("b7.amount")}><Input dir="ltr" inputMode="decimal" value={f.amount} onChange={set("amount")} data-testid="ledger-amount" className={cls} /></Field>
          <Field label={t("b7.reason")}><Input value={f.reason} onChange={set("reason")} data-testid="ledger-reason" className={cls} /></Field>
          {kind === "payment" && (
            <div className="grid grid-cols-2 gap-2">
              <Field label={t("b7.method")}><Input value={f.method} onChange={set("method")} data-testid="ledger-method" className={cls} /></Field>
              <Field label={t("b7.reference")}><Input value={f.reference} onChange={set("reference")} data-testid="ledger-reference" className={cls} /></Field>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("b7.date")}><DateField value={f.date} max={todayInBaghdad()} clearable onChange={(v) => setF({ ...f, date: v })} testId="ledger-date" /></Field>
            <Field label={t("b7.bookingRef")}><Input dir="ltr" value={f.booking} onChange={set("booking")} data-testid="ledger-booking" className={cls} /></Field>
          </div>
          {kind === "payment" && (
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={other} onChange={(e) => setOther(e.target.checked)} data-testid="ledger-other-currency" className="h-4 w-4" />{t("b7.otherCurrency")} ({otherCur})
            </label>
          )}
          {kind === "payment" && other && (
            <div className="grid grid-cols-2 gap-2 rounded-2xl border border-dashed p-3">
              <Field label={`${t("b7.settleAmount")} (${otherCur})`}><Input dir="ltr" value={st.amount} onChange={(e: any) => setSt({ ...st, amount: e.target.value })} data-testid="ledger-settle-amount" className={cls} /></Field>
              <Field label={t("b7.rate")}><Input dir="ltr" value={st.rate} onChange={(e: any) => setSt({ ...st, rate: e.target.value })} data-testid="ledger-settle-rate" className={cls} /></Field>
              <Field label={t("b7.direction")}><Input dir="ltr" value={st.direction} onChange={(e: any) => setSt({ ...st, direction: e.target.value })} data-testid="ledger-settle-direction" className={cls} /></Field>
              <Field label={t("b7.approvedBy")}><Input value={st.approved_by} onChange={(e: any) => setSt({ ...st, approved_by: e.target.value })} data-testid="ledger-settle-approved" className={cls} /></Field>
            </div>
          )}
          <button onClick={save} disabled={busy || !f.amount.trim() || f.reason.trim().length < 3} data-testid="ledger-save"
            className="w-full h-11 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50">{busy ? t("common.loading") : t("b7.save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
